"use client";

import { Atom, Beaker, Box, Cpu, Droplet, Flame, Layers, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ClinicalFeatures() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const features = [{
    name: "Surgical Precision",
    description: "Advanced surgical instrumentation sourced from global leaders to ensure optimal clinical outcomes.",
    metric: "ISO 13485",
    status: "Certified",
    Icon: Layers
  }, {
    name: "Diagnostic Accuracy",
    description: "High-fidelity diagnostic imaging and testing equipment for early and precise patient assessment.",
    metric: "CE/FDA",
    status: "Validated",
    Icon: Droplet
  }, {
    name: "Aesthetic Solutions",
    description: "Premium dermatological and aesthetic technologies designed for safety and patient satisfaction.",
    metric: "Clinical",
    status: "Proven",
    Icon: Beaker
  }, {
    name: "Dental Innovation",
    description: "State-of-the-art dental systems facilitating efficient workflows and superior restorative results.",
    metric: "Global",
    status: "Standard",
    Icon: Box
  }, {
    name: "AI Healthcare",
    description: "Next-generation artificial intelligence platforms for predictive analytics and clinical decision support.",
    metric: "Neural",
    status: "Integrated",
    Icon: Atom
  }, {
    name: "Digital Health",
    description: "Connected medical ecosystems enabling seamless data exchange and remote patient monitoring.",
    metric: "Secure",
    status: "Compliant",
    Icon: Shield
  }, {
    name: "Veterinary Care",
    description: "Specialized medical devices adapted for veterinary medicine, ensuring high-quality animal healthcare.",
    metric: "Vet-Grade",
    status: "Reliable",
    Icon: Cpu
  }, {
    name: "General Medicine",
    description: "Comprehensive range of essential medical supplies and equipment for diverse clinical environments.",
    metric: "Universal",
    status: "Essential",
    Icon: Flame
  }];
  return <section data-section-id="2254" id="clinicalfeaturesgrid" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-default text-xs md:text-sm tracking-[0.2em] uppercase mb-6 text-blue-600">
              Our Expertise
            </p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-gray-900">
              Innovative Medical Solutions.
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="font-default text-base md:text-lg leading-relaxed text-gray-600 max-w-xl">
              MS Biotechnology connects healthcare professionals with premium, clinically evaluated medical technologies from leading global manufacturers, ensuring safety, reliability, and long-term clinical value.
            </p>
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {features.map((feature, i) => {
          const Icon = feature.Icon;
          return <div data-index={i} key={feature.name} className="group relative rounded-lg border border-slate-200 bg-white p-7 md:p-8 transition-all duration-300 ease-out hover:-translate-y-1 shadow-md">
                <div className="mb-7 inline-flex h-[60px] w-[60px] items-center justify-center rounded-lg transition-colors duration-300 bg-teal-50">
                  <Icon className="h-6 w-6 transition-colors duration-300 text-teal-600" strokeWidth={1.5} />
                </div>

                <h3 className="font-default font-semibold text-2xl leading-tight text-gray-900 mb-3">
                  {feature.name}
                </h3>

                <p className="font-default text-sm leading-relaxed text-gray-600 mb-8">
                  {feature.description}
                </p>

                <div className="pt-5 border-t border-slate-100">
                  <p className="font-default text-xs tracking-wider uppercase">
                    <span className="text-blue-600 font-bold">{feature.metric}:</span>{" "}
                    <span className="text-gray-900">{feature.status}</span>
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}